/* eslint-disable react-refresh/only-export-components -- exposes hook and provider from shared context module */
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { useToast } from '@/hooks/use-toast';
import { setSentryUser, clearSentryContext } from '@/lib/sentry';
import * as Sentry from '@sentry/react';
import { TRPCClientError } from '@trpc/client';
import { trpc } from '@/lib/trpc';
import i18next from 'i18next';
import { isSupportedLang } from '@/lib/i18n';
import { isAdminRole } from '@/lib/roles';
import { parseUiPreferences, type UiPreferences } from '@threadheads/shared';

interface Profile {
  id: string;
  organization_id: string; // derived from membership or selection
  email: string;
  first_name: string;
  last_name: string;
  role: 'Super Admin' | 'Admin' | 'Warehouse Coordinator' | 'Warehouse Staff' | 'Viewer' | 'Picker' | 'Printer' | 'Catcher' | 'Packer' | 'CX' | 'DTF Printer'; // derived from membership
  is_active: boolean;
  preferred_language: 'en' | 'zh';
  /**
   * Per-user sidebar page allowlist for the Viewer role. Empty for non-Viewers.
   * See `apps/frontend/src/lib/page-registry.ts` for the canonical page IDs.
   */
  viewer_visible_pages: string[];
  /**
   * On the platform admin list (Blue Monk people, BLU-1313). Shows platform menus
   * and opens platform routes only; every platform call is re-checked server side.
   * Not the 'Super Admin' role: that role keeps only its in-org meaning.
   */
  is_platform_admin: boolean;
  locations: Array<{
    id: string;
    name: string;
    city?: string | null;
    country_code?: string | null;
    shopify_location_id?: number;
    timezone?: string | null;
    is_active?: boolean | null;
    enable_auto_availability?: boolean | null;
    organization_id?: string;
  }>;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<Profile | null>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  /**
   * Per-user display settings (profiles.ui_preferences), refilled from every
   * getProfile payload and `{}` after sign-out. Kept in its OWN slot, never on
   * `profile`: OrganizationContext refetches the organization whenever
   * `profile` changes, so a settings save must never replace it.
   */
  uiPreferences: UiPreferences;
  /** Replace the local display settings (stable). Saving to the server is the caller's job. */
  setUiPreferences: (prefs: UiPreferences) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const PROFILE_FETCH_TIMEOUT_MS = 20_000;
const PROFILE_FETCH_MAX_RETRIES = 2;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Global supabase.auth.signOut() that swallows AuthSessionMissingError.
 *
 * The Supabase admin /logout call requires an in-memory session. When the
 * session has already been cleared (e.g. auto-refresh failed and emitted
 * SIGNED_OUT before this code ran), the call throws AuthSessionMissingError
 * even though the cleanup we actually want has already happened. That noise
 * was the bulk of FRONTEND-62 in Sentry. Treat it as a successful no-op and
 * propagate any other failure to the caller.
 */
const signOutGloballySafely = async () => {
  try {
    return await supabase.auth.signOut();
  } catch (error) {
    if (error instanceof Error && error.name === 'AuthSessionMissingError') {
      return { error: null };
    }
    throw error;
  }
};

const isRetryableProfileFetchError = (error: unknown): boolean => {
  if (error instanceof TRPCClientError) {
    const code = error.data?.code;
    if (code === 'UNAUTHORIZED' || code === 'FORBIDDEN' || code === 'BAD_REQUEST' || code === 'NOT_FOUND') {
      return false;
    }

    const httpStatus = typeof error.data?.httpStatus === 'number' ? error.data.httpStatus : undefined;
    if (httpStatus && httpStatus >= 400 && httpStatus < 500 && httpStatus !== 408 && httpStatus !== 429) {
      return false;
    }
    return true;
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('timeout') ||
      message.includes('failed to fetch') ||
      message.includes('network') ||
      message.includes('fetch failed') ||
      message.includes('503') ||
      message.includes('502') ||
      message.includes('500')
    );
  }

  return false;
};


export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [uiPreferences, setUiPreferencesState] = useState<UiPreferences>({});
  const setUiPreferences = useCallback((prefs: UiPreferences) => setUiPreferencesState(prefs), []);
  const [loading, setLoading] = useState(true);
  const { toast, dismiss } = useToast();
  const trpcUtils = trpc.useUtils();
  const currentUserId = useRef<string | null>(null);
  const isSigningIn = useRef(false);
  const profileRef = useRef<Profile | null>(null);
  const bootstrapRecoveryAttemptsRef = useRef(0);
  // Track in-flight profile fetch so concurrent callers can await the same promise
  const inflightFetch = useRef<Promise<Profile | null> | null>(null);

  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  const fetchProfile = useCallback(async (
    userId: string,
    {
      force = false,
      silent = false,
      retries = PROFILE_FETCH_MAX_RETRIES,
    }: { force?: boolean; silent?: boolean; retries?: number } = {}
  ) => {
      // If a fetch is already in-flight, await it instead of returning null
      if (inflightFetch.current) {
        return inflightFetch.current;
      }

      // Prevent fetching if we already have this user's profile
      if (!force && currentUserId.current === userId && profileRef.current?.id === userId) {
        return profileRef.current;
      }

      const doFetch = async (): Promise<Profile | null> => {
        const maxAttempts = Math.max(1, retries + 1);

        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          let timeoutId: ReturnType<typeof setTimeout> | undefined;

          try {
            const timeoutPromise = new Promise<never>((_, reject) => {
              timeoutId = setTimeout(() => reject(new Error('Profile fetch timeout')), PROFILE_FETCH_TIMEOUT_MS);
            });

            const profileResponse = await Promise.race([
              trpcUtils.users.getProfile.fetch(),
              timeoutPromise,
            ]) as { profile: Profile; memberships: Array<{ organization_id: string; role: Profile['role'] | string; is_active: boolean }> };

            const profileData = profileResponse?.profile;
            const memberships = profileResponse?.memberships ?? [];

            if (!profileData) {
              throw new Error('No profile data returned from backend');
            }

            if (memberships.length === 0) {
              try {
                localStorage.removeItem('activeOrgId');
                localStorage.removeItem('threadheads-selected-location');
                localStorage.removeItem('threadheads-selected-location-id');
              } catch { /* ignore */ }
              setProfile(null);
              setUiPreferencesState({});
              profileRef.current = null;
              currentUserId.current = null;
              return null;
            }

            const storedOrgId =
              localStorage.getItem('activeOrgId') || localStorage.getItem('selected_org_id') || '';

            let orgId = profileData.organization_id || '';
            if (!orgId && memberships.length > 0) {
              orgId = memberships[0]?.organization_id ?? '';
            }

            if (orgId) {
              try { localStorage.setItem('activeOrgId', orgId); } catch { /* ignore */ }
              if (storedOrgId && storedOrgId !== orgId) {
                window.dispatchEvent(new CustomEvent('org-changed', { detail: { organizationId: orgId } }));
              }
            }

            const profilePayload: Profile = {
              id: profileData.id,
              organization_id: orgId || '',
              email: profileData.email,
              first_name: profileData.first_name,
              last_name: profileData.last_name,
              role: profileData.role ?? 'Warehouse Staff',
              is_active: profileData.is_active,
              preferred_language: isSupportedLang(profileData.preferred_language) ? profileData.preferred_language : 'en',
              locations: profileData.locations ?? [],
              viewer_visible_pages: Array.isArray(profileData.viewer_visible_pages)
                ? profileData.viewer_visible_pages
                : [],
              // Strict: an older backend without the field must read as false.
              is_platform_admin: profileData.is_platform_admin === true,
            };

            setProfile(profilePayload);
            profileRef.current = profilePayload;
            // Tolerates undefined from an older backend.
            setUiPreferencesState(
              parseUiPreferences((profileData as { ui_preferences?: unknown }).ui_preferences),
            );
            currentUserId.current = userId;

            // Set initial Sentry user context (location will be set by LocationContext)
            try {
              setSentryUser(profilePayload.id, undefined, undefined, profilePayload.email, profilePayload.role);
            } catch (_error) {
              // Sentry error tracking failed - non-critical
            }

            return profilePayload;
          } catch (error) {
            // If we got a 401, the access token may simply have expired or been
            // briefly rejected under auth-service load. Try refreshing the session
            // once before signing out — a successful refresh lets the operator
            // keep working through the next attempt of this loop. We only fall
            // through to a hard signOut when the refresh itself fails (no
            // refresh token / refresh returns 401), which is the genuinely
            // stale-session case.
            const is401 =
              (error instanceof TRPCClientError && error.data?.code === 'UNAUTHORIZED') ||
              (error instanceof Error &&
                (error.message.includes('401') || error.message.includes('UNAUTHORIZED')));
            if (is401) {
              const canRetryAfterRefresh = attempt < maxAttempts - 1;
              if (canRetryAfterRefresh) {
                const refreshOk = await supabase.auth.refreshSession()
                  .then(({ data, error: refreshError }) => !refreshError && !!data?.session)
                  .catch(() => false);
                if (refreshOk) {
                  console.warn('[AuthContext] 401 recovered via refreshSession – retrying profile fetch');
                  await delay(200);
                  continue;
                }
              }

              console.warn('[AuthContext] Stale session (401, refresh failed) – signing out');
              setProfile(null);
              setUiPreferencesState({});
              profileRef.current = null;
              setUser(null);
              setSession(null);
              currentUserId.current = null;
              // Use local scope to just clear localStorage – the auth server may
              // be unreachable or have different keys (fresh local Supabase).
              await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
              return null;
            }

            const canRetry = attempt < maxAttempts - 1 && isRetryableProfileFetchError(error);
            if (canRetry) {
              await delay(400 * (attempt + 1));
              continue;
            }

            // Capture profile fetch error in Sentry
            Sentry.captureException(error, {
              tags: {
                component: 'AuthContext',
                operation: 'fetchProfile',
                userId: userId
              },
              extra: {
                error: error instanceof Error ? error.message : String(error),
                attempts: maxAttempts,
              },
              level: 'error'
            });

            // Only show toast for background fetches (not during signIn which handles its own errors)
            if (!silent) {
              toast({
                title: i18next.t('common:error'),
                description: i18next.t('auth:profileFetchError'),
                variant: 'destructive',
              });
            }
            return null;
          } finally {
            if (timeoutId) clearTimeout(timeoutId);
          }
        }

        return null;
      };

      inflightFetch.current = doFetch().finally(() => {
        inflightFetch.current = null;
      });
      return inflightFetch.current;
    }, [toast, trpcUtils]);

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  useEffect(() => {
    let isInitialLoad = true;

    // Get initial session and validate it against the auth server.
    // getSession() reads from localStorage and may return a stale token
    // (e.g., from a different Supabase instance). getUser() makes a network
    // call to verify the token is actually valid.
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        // Validate the session is still valid with the current auth server
        const { data: { user: validatedUser }, error } = await supabase.auth.getUser();
        if (error || !validatedUser) {
          // Stale session – clear it locally without hitting the auth server
          console.warn('[AuthContext] Stale session in localStorage – clearing');
          await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
          setSession(null);
          setUser(null);
          setLoading(false);
          isInitialLoad = false;
          return;
        }
        setSession(session);
        setUser(validatedUser);
        fetchProfile(validatedUser.id, { silent: true }).finally(() => {
          setLoading(false);
          isInitialLoad = false;
        });
      } else {
        setSession(null);
        setUser(null);
        setLoading(false);
        isInitialLoad = false;
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Avoid duplicate profile fetch on initial session hydration.
      if (event === 'INITIAL_SESSION') {
        return;
      }

      // Skip the initial SIGNED_IN event if we're still processing the initial load
      if (isInitialLoad && event === 'SIGNED_IN') {
        return;
      }

      // Only update if the user actually changed
      const userChanged = session?.user?.id !== currentUserId.current;

      setSession(session);
      setUser(session?.user ?? null);

      if (event === 'SIGNED_IN' && isSigningIn.current) {
        return;
      }

      if (session?.user && userChanged) {
        setLoading(true);
        const userId = session.user.id;
        // Never await auth-dependent work inside this listener: Supabase awaits
        // listeners while holding its lock / startup promise, and fetchProfile
        // needs that lock (tRPC headers -> getSession; its 401 path calls
        // refreshSession/signOut), so awaiting here deadlocked until the profile
        // timeout (~60s). setTimeout(0) is Supabase's documented pattern.
        setTimeout(() => {
          void fetchProfile(userId, { silent: true }).finally(() => setLoading(false));
        }, 0);
      } else if (!session) {
        setProfile(null);
        setUiPreferencesState({});
        currentUserId.current = null;

        // Clear Sentry context on sign out
        try {
          clearSentryContext();
        } catch (_error) {
          // Sentry context clear failed - non-critical
        }

        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  // Refresh profile when organization changes via OrgSwitcher
  useEffect(() => {
    const handler = () => {
      if (user?.id) {
        fetchProfile(user.id, { force: true });
      }
    };
    window.addEventListener('org-changed', handler);
    return () => window.removeEventListener('org-changed', handler);
  }, [user?.id, fetchProfile]);

  // If startup profile bootstrap fails, keep retrying in the background so users
  // don't get stuck indefinitely on the loading screen until a manual refresh.
  useEffect(() => {
    if (loading || !user?.id || profile) {
      bootstrapRecoveryAttemptsRef.current = 0;
      return;
    }

    let cancelled = false;
    const maxAttempts = 4;

    const recoverProfile = async () => {
      while (!cancelled && !profileRef.current && bootstrapRecoveryAttemptsRef.current < maxAttempts) {
        bootstrapRecoveryAttemptsRef.current += 1;
        const waitMs = 600 * bootstrapRecoveryAttemptsRef.current;
        await delay(waitMs);
        if (cancelled) return;
        await fetchProfile(user.id, { force: true, silent: true, retries: 1 });
      }

      // Final fallback: clear the stale local session instead of leaving the user
      // trapped forever on "Loading user profile...".
      if (!cancelled && !profileRef.current) {
        console.warn('[AuthContext] Unable to load profile after bootstrap retries; clearing local session');
        await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
        setSession(null);
        setUser(null);
        setProfile(null);
        setUiPreferencesState({});
        profileRef.current = null;
        currentUserId.current = null;
      }
    };

    void recoverProfile();

    return () => {
      cancelled = true;
    };
  }, [loading, user?.id, profile, fetchProfile]);

  const signIn = async (email: string, password: string) => {
    try {
      // Dismiss any lingering toasts (e.g. "Successfully signed out") before showing sign-in result
      dismiss();
      isSigningIn.current = true;
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (!data.user) {
        throw new Error('No user returned from sign in');
      }

      // Clear stale org ID so backend falls back to first active membership
      try { localStorage.removeItem('activeOrgId'); } catch { /* ignore */ }

      const profile = await fetchProfile(data.user.id, { force: true, silent: true });

      if (!profile) {
        await signOutGloballySafely();
        throw new Error(i18next.t('auth:unableToLoadProfile'));
      }

      if (!profile.is_active) {
        await signOutGloballySafely();
        throw new Error(i18next.t('auth:accountDeactivated'));
      }

      // Check if user has location access. Not required for Admin / Super Admin,
      // which have org-wide access by role rather than per-location assignment.
      // Uses the shared ADMIN_ROLES set (via isAdminRole) so this gate can't
      // drift from the permission model — a Super Admin with zero locations was
      // previously locked out here with no way back in.
      if (!isAdminRole(profile.role) && profile.locations.length === 0) {
        await signOutGloballySafely();
        throw new Error(i18next.t('auth:noLocationAccess'));
      }

      toast({
        title: i18next.t('common:success'),
        description: i18next.t('auth:signInSuccess'),
      });

      return profile;
    } catch (error) {
      // Capture sign in error in Sentry
      Sentry.captureException(error, {
        tags: {
          component: 'AuthContext',
          operation: 'signIn'
        },
        extra: {
          email,
          error: error instanceof Error ? error.message : String(error)
        },
        level: 'error'
      });

      toast({
        title: i18next.t('common:error'),
        description: error instanceof Error ? error.message : i18next.t('auth:signInError'),
        variant: 'destructive',
      });
      throw error;
    } finally {
      isSigningIn.current = false;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await signOutGloballySafely();
      if (error) throw error;

      setProfile(null);
      setUiPreferencesState({});
      profileRef.current = null;
      currentUserId.current = null;
      inflightFetch.current = null;

      // Clear Sentry context on explicit sign out
      try {
        clearSentryContext();
      } catch (_error) {
        // Sentry context clear failed - non-critical
      }

      toast({
        title: i18next.t('common:success'),
        description: i18next.t('auth:signOutSuccess'),
      });
    } catch (error) {
      // Capture sign out error in Sentry
      Sentry.captureException(error, {
        tags: {
          component: 'AuthContext',
          operation: 'signOut'
        },
        extra: {
          error: error instanceof Error ? error.message : String(error)
        },
        level: 'warning' // Lower severity for sign out failures
      });
      
      toast({
        title: i18next.t('common:error'),
        description: error instanceof Error ? error.message : i18next.t('auth:signOutError'),
        variant: 'destructive',
      });
      throw error;
    }
  };

  const value = {
    user,
    session,
    profile,
    loading,
    signIn,
    signOut,
    refreshProfile,
    uiPreferences,
    setUiPreferences,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
